import type { LlamoxPlan, Workspace } from "./types";
import { LlamoxApiError } from "./types";

export const ACTIVE_WORKSPACE_KEY = "llamox.activeWorkspaceId";
export const ACTIVE_WORKSPACE_COOKIE = "llamox_active_workspace_id";
export const ACTIVE_WORKSPACE_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

export type WorkspaceOverlay = {
  workspaceId: string;
  name?: string;
  slug?: string;
  plan?: LlamoxPlan | string;
  description?: string;
  logoUrl?: string;
  lastActivity?: number;
  deletionStatus?: Workspace["deletionStatus"];
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;
  currentPeriodEnd?: number;
};

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

export function storageKey(scope: string): string {
  return `${ACTIVE_WORKSPACE_KEY}.${scope}`;
}

export function writeActiveWorkspaceCookie(id: string) {
  if (typeof document === "undefined") return;
  const encoded = encodeURIComponent(id);
  const maxAge = id ? ACTIVE_WORKSPACE_COOKIE_MAX_AGE : 0;
  document.cookie = `${ACTIVE_WORKSPACE_COOKIE}=${encoded}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function readStoredId(scope: string): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(storageKey(scope)) || "";
  } catch {
    return "";
  }
}

export function writeStoredId(id: string, scope: string) {
  if (typeof window === "undefined") return;
  try {
    if (id) {
      window.localStorage.setItem(storageKey(scope), id);
    } else {
      window.localStorage.removeItem(storageKey(scope));
    }
    window.localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    writeActiveWorkspaceCookie(id);
  } catch {
    /* Storage and cookie access can be blocked by browser privacy settings. */
  }
}

export function slugSuffix(input: string): string {
  return (
    input
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(-8) || Math.random().toString(36).slice(2, 8)
  );
}

export function appendSlugSuffix(base: string, suffix: string): string {
  const cleanSuffix = slugSuffix(suffix);
  const maxBaseLength = Math.max(1, 49 - cleanSuffix.length);
  return `${base.slice(0, maxBaseLength).replace(/-+$/g, "")}-${cleanSuffix}`;
}

export function isSlugConflict(error: unknown): boolean {
  if (!(error instanceof LlamoxApiError)) return false;
  const bodyText =
    typeof error.body === "string"
      ? error.body
      : JSON.stringify(error.body ?? {});
  const text = `${error.message} ${bodyText}`.toLowerCase();
  return text.includes("slug") && text.includes("already");
}

export function mergeWithOverlay(
  base: Workspace | null,
  overlay: WorkspaceOverlay | null,
): Workspace | null {
  if (!base) return null;
  if (!overlay) return base;
  return {
    ...base,
    name: overlay.name ?? base.name,
    slug: overlay.slug ?? base.slug,
    plan: (overlay.plan as LlamoxPlan | undefined) ?? base.plan,
    description: overlay.description ?? base.description,
    logoUrl: overlay.logoUrl ?? base.logoUrl,
    lastActivity: overlay.lastActivity,
    deletionStatus: overlay.deletionStatus,
    stripeCustomerId: overlay.stripeCustomerId ?? base.stripeCustomerId,
    stripeSubscriptionId:
      overlay.stripeSubscriptionId ?? base.stripeSubscriptionId,
    stripePriceId: overlay.stripePriceId ?? base.stripePriceId,
    currentPeriodEnd: overlay.currentPeriodEnd ?? base.currentPeriodEnd,
  };
}
